import React, { useEffect, useRef } from 'react';
import { ArrowRight, ChevronDown, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { heroContent } from '../data/mock';

const Hero = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 60;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
        this.color = Math.random() > 0.7 ? '79, 70, 229' : '6, 182, 212';
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Add glow effect
        ctx.shadowBlur = 15;
        ctx.shadowColor = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToResearch = () => {
    const element = document.querySelector('#research');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Animated Canvas Background */}
      <canvas 
        ref={canvasRef}
        className="absolute inset-0 z-0"
      />

      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${heroContent.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/98 via-indigo-950/95 to-slate-900/97"></div>
        <div className="absolute inset-0 grid-overlay opacity-20"></div>
      </div>

      {/* Background Image with blue overlay - NO floating shapes */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${heroContent.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/98 via-indigo-950/95 to-slate-900/97"></div>
        <div className="absolute inset-0 grid-overlay opacity-20"></div>
      </div>

      {/* Top - Main Title */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight text-center animate-fade-in-up" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.03em', lineHeight: '1.1' }}>
          Welcome to The <span className="whitespace-nowrap">Chaturvedi Lab</span>
        </h1>
      </div>

      {/* Bottom - Info Section - Absolute Position */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center space-y-6">
            {/* Gradient Subtitle */}
            <p 
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold animate-fade-in max-w-5xl" 
              style={{ 
                animationDelay: '0.2s', 
                fontFamily: "'Space Grotesk', sans-serif",
                background: 'linear-gradient(135deg, #8B5CF6 0%, #06B6D4 50%, #3B82F6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: '1.3'
              }}
            >
              {heroContent.subtitle}
            </p>
            
            {/* Mission Statement */}
            <p className="text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed font-medium max-w-5xl">
              {heroContent.description}
            </p>

            {/* Discover More Button */}
            <button 
              onClick={scrollToResearch}
              className="text-cyan-400 hover:text-cyan-300 transition-colors group py-3 px-4 mt-4"
              aria-label="Scroll down"
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              <span className="text-base font-semibold tracking-wide">DISCOVER MORE</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
